import React, { TransitionEventHandler, useCallback, useEffect, useState, useMemo } from "react";
import { useLocation } from "@reach/router";
import { navigate } from "gatsby";

import {
    panel,
    panelAdmin,
    header,
    footer,
    closeButton,
    panel__contentWrapper,
    userCard,
    userCardAvatar,
    userCardAvatarImg,
    userCardTitle,
    userCardDescription,
    userCardButton,
    accountNav,
    navItem,
    navItemLogout,
    navItemActive,
} from "./side-panel.module.scss";
import { config } from "../../config";
import { getAbsolutePath } from "../../apps/utility/routes.utility";
import useCheckPermissions from "../../apps/hooks/use-check-permissions";
import useMenuClosingButton from "../../hooks/use-menu-closing-button";
import useUser from "../../apps/auth/hooks/use-user";
import useSurveySession from "../../apps/hooks/use-survey-session";

import Button from "../atoms/button";
import MenuCloseButton from "../atoms/menu-close-button";
import Logo from "../atoms/logo";
import Link from "../atoms/link";
import UserAvatar from "../atoms/user-avatar";
import { normalizeApiFileUrl } from "../../utils/api-url";

const { accountLinks, rbac } = config;

type MenuLinksElement = {
    labelKey: string;
    to: string;
    pathname?: string;
    param?: string | number;
    onClick?: () => void;
    customClass?: string;
};

export interface ISidePanelProps {
    className?: string;
    onClose?: () => void;
}

function NavItem({ labelKey, to, pathname, param, onClick, customClass, isLogout }: MenuLinksElement & { isLogout?: boolean }) {
    const path = getAbsolutePath(to, undefined, param);
    
    // Poprawiona logika sprawdzania aktywnego elementu
    const isActive = useMemo(() => {
        if (!pathname || !path) return false;
        
        // Normalizuj ścieżki (usuń końcowy slash)
        const normalizedPathname = pathname.replace(/\/$/, "");
        const normalizedPath = path.replace(/\/$/, "");
        
        // Dla ankiet sprawdzamy czy pathname zawiera /konto/ankieta
        if (to === "ACCOUNT_SURVEY_SESSION") {
            return normalizedPathname.includes("/konto/ankieta");
        }
        
        // Dla panelu sprawdzamy czy jest /konto/panel lub dokładnie /konto
        if (to === "ACCOUNT_DASHBOARD") {
            return normalizedPathname === "/konto/panel" || normalizedPathname === "/konto";
        }

        // Panel startowy pokazuje listę stylizacji, więc zaznacz tę pozycję
        // również bez zmiany adresu na /konto/stylizacje.
        if (to === "ACCOUNT_STYLIZATIONS") {
            return normalizedPathname === "/konto/panel" ||
                normalizedPathname === "/konto" ||
                normalizedPathname === normalizedPath;
        }
        
        // Dla pozostałych sprawdzamy czy pathname jest równy path lub zaczyna się od path/
        return normalizedPathname === normalizedPath || normalizedPathname.startsWith(normalizedPath + "/");
    }, [pathname, path, to]);

    const handleClick = () => {
        if (typeof onClick === "function") {
            onClick();
        }
        if (path) {
            navigate(path);
        }
    };

    return (
        <button
            className={`${navItem} ${isActive ? navItemActive : ""} ${isLogout ? navItemLogout : ""} ${customClass || ""}`}
            onClick={handleClick}
        >
            {labelKey}
        </button>
    );
}

export default function SidePanel({ className, onClose }: ISidePanelProps) {
    const location = useLocation();

    const isAdmin = useCheckPermissions(rbac.permissionMap.Admin.role);
    const isUser = useCheckPermissions(rbac.permissionMap.User.role);
    const { firstSessionId } = useSurveySession();

    const [navItems, setNavItems] = useState<MenuLinksElement[]>([]);

    const { animatedElementRef, closing, onAnimationEnd } = useMenuClosingButton(onClose);
    const { logOut, profile } = useUser();

    const [showAddStylization, setShowAddStylization] = useState(false);
    const handleMenuCloseButtonClick = () => {
        if (onClose) {
            onClose();
        }
    };

    const handleTransitionEnd: TransitionEventHandler = useCallback(
        (event) => {
            onAnimationEnd(event.target);
        },
        [onAnimationEnd]
    );

    useEffect(() => {
        setShowAddStylization(location.pathname.includes("/konto/stylizacje"));
    }, [location]);
    
    useEffect(() => {
        // console.log("location: " + location.pathname);
        if (isAdmin || isUser) {
            const links = accountLinks.map((link) => {
                if (link.to === "ACCOUNT_SURVEY_SESSION") {
                    return { ...link, param: firstSessionId };
                }
                return link;
            });

            if (isAdmin) {
                links.push({
                    labelKey: "Statystyki",
                    to: "ADM_DASHBOARD",
                });
                links.push({
                    labelKey: "Wypełnione ankiety",
                    to: "ADM_SURVEY_SESSION_LIST",
                });
            }

            setNavItems(links);
        }
    }, [isAdmin, isUser, firstSessionId]);

    const handleAddStylization = async () => {
        await navigate(getAbsolutePath("GENERATOR_NEW_STYLIZATION"));
    };

    const handleStylizeClick = async () => {
        await navigate(getAbsolutePath("GENERATOR_NEW_STYLIZATION"));
        if (onClose) {
            onClose();
        }
    };

    const userName = profile?.firstName || "";
    const avatarUri = normalizeApiFileUrl(profile?.avatarUri);

    return (
        <aside
            ref={animatedElementRef}
            className={`${panel} ${className} ${isAdmin ? panelAdmin : ""}`}
            onTransitionEnd={handleTransitionEnd}
        >
            <div className={panel__contentWrapper}>
                <div className={header}>
                    <MenuCloseButton
                        className={closeButton}
                        closing={closing}
                        onClick={() => handleMenuCloseButtonClick()}
                    />
                </div>
                <div className={userCard}>
                    <div className={userCardAvatar}>
                        {avatarUri ? (
                            <UserAvatar src={avatarUri} className={userCardAvatarImg} />
                        ) : (
                            <img 
                                className={userCardAvatarImg}
                                src="/images/sample-avatar.png" 
                                alt="Avatar użytkownika" 
                            />
                        )}
                    </div>
                    <h2 className={userCardTitle}>Cześć, {userName}!</h2>
                    <p className={userCardDescription}>Tu znajdziesz wszystkie swoje stylizacje, ankietę oraz ustawienia konta.</p>
                    <button className={userCardButton} type="button" onClick={handleStylizeClick}>
                        Wystylizuj mnie
                    </button>
                </div>
                <nav className={accountNav}>
                    {/* {showAddStylization && (
                        <NavItem 
                            labelKey="Dodaj stylizacje" 
                            to="" 
                            pathname={location.pathname}
                            onClick={() => {
                                handleAddStylization();
                                handleMenuCloseButtonClick();
                            }} 
                        />
                    )} */}
                    {navItems.map((item) => {
                        return (
                            <NavItem
                                key={item.to}
                                pathname={location.pathname}
                                to={item.to}
                                labelKey={item.labelKey}
                                param={item.param}
                                onClick={handleMenuCloseButtonClick}
                            />
                        );
                    })}
                    <NavItem 
                        labelKey="Wyloguj się" 
                        to="" 
                        pathname={location.pathname}
                        onClick={() => logOut()} 
                        isLogout={true}
                    />
                </nav>
            </div>
            <Link
                to="/"
                additionalProps={{ onClick: handleMenuCloseButtonClick }}
                className={footer}
            >
                <Logo color="white" type="logo" />
            </Link>
        </aside>
    );
}
